import { Completion, CompletionContext, pickedCompletion } from '@codemirror/autocomplete';
import { Diagnostic } from '@codemirror/lint';
import { EditorState, Transaction } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import {
    LspCompletionItem,
    LspCompletionTextEdit,
    LspDiagnostic,
    LspHover,
    LspPosition,
    LspRange,
    LspTextEdit
} from '../lsp/CSharpLspTypes';

export function formatHoverContents(contents?: LspHover['contents']): string {
    if (!contents) {
        return '';
    }

    if (typeof contents === 'string') {
        return contents;
    }

    if (Array.isArray(contents)) {
        return contents
            .map(value => formatHoverContents(value))
            .filter(Boolean)
            .join('\n\n');
    }

    if (typeof contents === 'object' && 'value' in contents) {
        return contents.value;
    }

    return '';
}

export function getCompletionReplacement(context: CompletionContext): { from: number; validFor: RegExp } | null {
    const identifier = context.matchBefore(/\w+/);
    const from = identifier ? identifier.from : context.pos;
    const previousCharacter = from > 0 ? context.state.sliceDoc(from - 1, from) : '';
    const triggeredByMemberAccess = previousCharacter === '.';

    if (!context.explicit && !identifier && !triggeredByMemberAccess) {
        return null;
    }

    return {
        from,
        validFor: /^\w*$/
    };
}

export function offsetToPosition(state: EditorState, offset: number): LspPosition {
    const line = state.doc.lineAt(offset);
    return {
        character: offset - line.from,
        line: line.number - 1
    };
}

export function positionToOffset(state: EditorState, position: LspPosition): number {
    const safeLineNumber = Math.min(Math.max(position.line + 1, 1), state.doc.lines);
    const line = state.doc.line(safeLineNumber);
    return Math.min(line.from + position.character, line.to);
}

export function positionToOffsetInText(documentText: string, position: LspPosition): number {
    const lines = documentText.split('\n');
    const safeLineIndex = Math.min(Math.max(position.line, 0), Math.max(lines.length - 1, 0));
    let offset = 0;

    for (let lineIndex = 0; lineIndex < safeLineIndex; lineIndex += 1) {
        offset += lines[lineIndex].length + 1;
    }

    return offset + Math.min(position.character, lines[safeLineIndex].length);
}

export function toCodeMirrorCompletion(item: LspCompletionItem): Completion {
    const label = typeof item.label === 'string' ? item.label : item.label.label;
    const detail = item.detail || (typeof item.label === 'object' ? item.label.detail || item.label.description : '');
    const documentation = formatHoverContents(item.documentation);
    const apply = item.textEdit || item.additionalTextEdits?.length
        ? (view: EditorView, completion: Completion, from: number, to: number) => {
              view.dispatch({
                  annotations: [pickedCompletion.of(completion), Transaction.userEvent.of('input.complete')],
                  changes: toCompletionChanges(view.state, item, label, from, to)
              });
          }
        : item.insertText || label;

    return {
        apply,
        detail: detail || undefined,
        info: documentation || undefined,
        label,
        type: toCompletionType(item.kind)
    };
}

export function toCodeMirrorDiagnostic(documentText: string, diagnostic: LspDiagnostic): Diagnostic {
    const documentLength = documentText.length;
    const from = positionToOffsetInText(documentText, diagnostic.range.start);
    const to = positionToOffsetInText(documentText, diagnostic.range.end);
    const normalizedRange = normalizeRange(from, to, documentLength);

    return {
        from: normalizedRange.from,
        message: diagnostic.message,
        severity: toDiagnosticSeverity(diagnostic.severity),
        source: diagnostic.source,
        to: normalizedRange.to
    };
}

export function toTooltipBounds(
    state: EditorState,
    range: LspRange | undefined,
    fallbackPosition: number
): {
    end: number;
    pos: number;
} {
    const from = range ? positionToOffset(state, range.start) : fallbackPosition;
    const to = range ? positionToOffset(state, range.end) : fallbackPosition;
    const normalizedRange = normalizeRange(from, to, state.doc.length);

    return {
        end: normalizedRange.to,
        pos: normalizedRange.from
    };
}

function normalizeRange(
    from: number,
    to: number,
    documentLength: number
): {
    from: number;
    to: number;
} {
    const safeFrom = Math.min(Math.max(from, 0), documentLength);
    const safeTo = Math.min(Math.max(to, safeFrom), documentLength);

    return {
        from: safeFrom,
        to: safeTo > safeFrom ? safeTo : safeFrom < documentLength ? safeFrom + 1 : safeFrom
    };
}

function toTextEditChange(
    state: EditorState,
    textEdit: LspTextEdit | LspCompletionTextEdit
): {
    from: number;
    insert: string;
    to: number;
} {
    const range = 'replace' in textEdit ? textEdit.replace : textEdit.range;

    return {
        from: positionToOffset(state, range.start),
        insert: textEdit.newText,
        to: positionToOffset(state, range.end)
    };
}

function toCompletionChanges(
    state: EditorState,
    item: LspCompletionItem,
    label: string,
    from: number,
    to: number
): Array<{
    from: number;
    insert: string;
    to: number;
}> {
    const primaryChange = item.textEdit
        ? toTextEditChange(state, item.textEdit)
        : {
              from,
              insert: item.insertText || label,
              to
          };

    return [primaryChange, ...(item.additionalTextEdits || []).map(edit => toTextEditChange(state, edit))]
        .sort((left, right) => left.from - right.from || left.to - right.to);
}

function toCompletionType(kind?: number): string {
    switch (kind) {
        case 2:
        case 3:
        case 7:
            return 'method';
        case 5:
        case 6:
        case 10:
            return 'property';
        case 8:
            return 'class';
        case 9:
            return 'interface';
        case 14:
            return 'keyword';
        case 17:
            return 'namespace';
        case 22:
            return 'enum';
        default:
            return 'text';
    }
}

function toDiagnosticSeverity(severity?: number): Diagnostic['severity'] {
    switch (severity) {
        case 1:
            return 'error';
        case 2:
            return 'warning';
        case 3:
            return 'info';
        default:
            return 'hint';
    }
}
